/**
 * Naves.ts — Sprites das naves (Fase 6): dardo com brilho assado na textura,
 * nariz virado pro rumo (tangente da orbita ou direcao do voo), rastro em
 * transito e INTERPOLACAO entre os ticks do servidor (20 Hz) pra andar liso a
 * 60 fps. Sem filtro por nave: 500 sprites entram num unico batch.
 *
 * Spawn anima de scale 0 -> 1. Morte anima scale 1 -> 1.8 + fade.
 */

import { Sprite } from 'pixi.js';
import gsap from 'gsap';
import { corPlaneta } from '../config';
import { ESTADO_EM_TRANSITO, type Nave, type EstadoJogo } from '../domain/tipos';
import type { NaveTupla, NaveServidorObjeto } from '../ws/protocolo';
import type { PixiApp } from './PixiApp';
import { texturaNave, texturaRastro } from './Texturas';

const ESCALA = 0.62;
const ESCALA_RASTRO_X = 0.55;

export class Naves {
  constructor(
    private readonly pixi: PixiApp,
    private readonly estado: EstadoJogo,
  ) {}

  /** Liga a interpolacao (chamar uma vez, depois do Pixi nascer). */
  iniciar(): void {
    this.pixi.addTicker((t) => this.interpolar(t.deltaMS));
  }

  /** Aceita tupla compactada (msg `estado`) ou objeto (partida_inicio). */
  adicionarOuAtualizar(naveData: NaveTupla | NaveServidorObjeto): Nave {
    const d = decodificar(naveData);
    let nave = this.estado.naves.get(d.id);
    if (!nave) {
      const sprite = new Sprite(texturaNave(corPlaneta(d.donoId)));
      sprite.anchor.set(0.5);
      sprite.x = d.x;
      sprite.y = d.y;
      nave = { id: d.id, donoId: d.donoId, estado: d.estado, x: d.x, y: d.y, hp: d.hp, contextId: d.contextId, angulo: d.angulo, rumo: rumoOrbital(d.angulo), sprite };
      sprite.rotation = nave.rumo;
      this.pixi.navesLayer.addChild(sprite);
      this.estado.naves.set(d.id, nave);
      sprite.scale.set(0);
      gsap.to(sprite.scale, { x: ESCALA, y: ESCALA, duration: 0.3, ease: 'back.out' });
      return nave;
    }

    // Rumo: em transito = direcao do deslocamento; orbitando = tangente da orbita
    if (d.estado === ESTADO_EM_TRANSITO) {
      const dx = d.x - nave.x;
      const dy = d.y - nave.y;
      if (dx * dx + dy * dy > 0.25) nave.rumo = Math.atan2(dy, dx);
    } else {
      nave.rumo = rumoOrbital(d.angulo);
    }
    nave.x = d.x;
    nave.y = d.y;
    nave.hp = d.hp;
    nave.estado = d.estado;
    nave.contextId = d.contextId;
    nave.angulo = d.angulo;
    if (nave.donoId !== d.donoId) {
      // (nao acontece hoje: nave nao muda de dono; deixado por seguranca)
      nave.donoId = d.donoId;
      if (nave.sprite instanceof Sprite) nave.sprite.texture = texturaNave(corPlaneta(d.donoId));
    }
    this.ajustarRastro(nave);
    return nave;
  }

  remover(id: number): void {
    const nave = this.estado.naves.get(id);
    if (!nave) return;
    if (nave.rastro) {
      const r = nave.rastro;
      if (r.parent) r.parent.removeChild(r);
      r.destroy();
      nave.rastro = undefined;
    }
    if (nave.sprite) {
      const sprite = nave.sprite;
      gsap.to(sprite.scale, { x: ESCALA * 1.9, y: ESCALA * 1.9, duration: 0.15, ease: 'power1.out' });
      gsap.to(sprite, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => {
          if (sprite.parent) sprite.parent.removeChild(sprite);
          sprite.destroy();
        },
      });
    }
    this.estado.naves.delete(id);
  }

  /** Rastro so enquanto voa; some ao chegar. */
  private ajustarRastro(nave: Nave): void {
    const voando = nave.estado === ESTADO_EM_TRANSITO;
    if (voando && !nave.rastro) {
      const r = new Sprite(texturaRastro(corPlaneta(nave.donoId)));
      r.anchor.set(1, 0.5);
      r.scale.set(ESCALA_RASTRO_X, 0.7);
      r.alpha = 0.7;
      r.blendMode = 'add';
      nave.rastro = r;
      this.pixi.navesLayer.addChildAt(r, 0);
    } else if (!voando && nave.rastro) {
      const r = nave.rastro;
      nave.rastro = undefined;
      gsap.to(r, { alpha: 0, duration: 0.25, onComplete: () => { if (r.parent) r.parent.removeChild(r); r.destroy(); } });
    }
  }

  /** A cada frame: sprite corre atras da posicao do servidor (lerp) e vira pro rumo. */
  private interpolar(dtMs: number): void {
    const k = Math.min(1, (dtMs / 1000) * 16);
    for (const nave of this.estado.naves.values()) {
      const s = nave.sprite;
      if (!s) continue;
      const dx = nave.x - s.x;
      const dy = nave.y - s.y;
      if (dx * dx + dy * dy > 90 * 90) {
        s.x = nave.x; s.y = nave.y;       // teleporte (chegada/captura): nao arrasta
      } else {
        s.x += dx * k;
        s.y += dy * k;
      }
      // Rotacao pelo caminho mais curto
      let dr = nave.rumo - s.rotation;
      while (dr > Math.PI) dr -= Math.PI * 2;
      while (dr < -Math.PI) dr += Math.PI * 2;
      s.rotation += dr * Math.min(1, k * 1.5);
      if (nave.rastro) {
        nave.rastro.x = s.x;
        nave.rastro.y = s.y;
        nave.rastro.rotation = s.rotation;
      }
    }
  }
}

/** Tangente da orbita: a nave anda no sentido do angulo crescente. */
function rumoOrbital(angulo: number): number {
  return angulo + Math.PI / 2;
}

interface NaveDecodificada {
  id: number;
  donoId: number;
  estado: number;
  x: number;
  y: number;
  hp: number;
  contextId: number;
  angulo: number;
}

function decodificar(data: NaveTupla | NaveServidorObjeto): NaveDecodificada {
  if (Array.isArray(data)) {
    return { id: data[0], donoId: data[1], estado: data[2], x: data[3], y: data[4], hp: data[5], contextId: data[6], angulo: data[7] ?? 0 };
  }
  return {
    id: data.id,
    donoId: data.dono_id,
    estado: data.estado,
    x: data.x,
    y: data.y,
    hp: data.hp,
    contextId: data.planeta_id ?? 0,
    angulo: data.angulo_orbital ?? 0,
  };
}
